import _ from 'lodash';

function isValidPoint(point, showZeros = true) {
    let validZeroValue = false;

    if (point) {
        validZeroValue = (point.value === 0 && showZeros === true) || point.value !== 0;
    }

    return point && _.isFinite(point.value) && validZeroValue;
}

function pointsWindow(series, callback) {
    const data = _.get(series, 'data');

    // if data is not an array or does not have a length, bail out since there is nothing
    // we can do
    if (!_.isArray(data) || !data.length) {
        return;
    }

    // can't use _.each here because data itself may change its length
    for (let i = 0; i < data.length; i += 1) {
        const ret = callback({
            prev: data[i - 1],
            curr: data[i],
            next: data[i + 1],
            index: i,
            length: data.length,
        });

        if (ret === false) {
            break;
        }
    }
}

export function singlePoint(series) {
    if (series.data?.length === 1) {
        const item = series.data[0];
        const item2 = _.clone(item);
        item2.x = item.x + 1;
        series.data = [item, item2];
    }
    return series;
}

export function connectIsolatedPointsNearest(series) {
    const granularity = series.granularity || 60_000;
    const yValue = 0;
    const xOffset = granularity / 4;

    pointsWindow(series, ({ prev, curr, next, index, length }) => {
        const prevIsInjected = _.get(prev, '$injected') === true;
        const nextIsInjected = _.get(next, '$injected') === true;
        const isFirstPoint = index === 0;
        const isLastPoint = index === length - 1;

        // we do not handle the case where there is only one point in a chart. There is another transformation for it. See
        // the duplicateSinglePoints transformation.
        if (length === 1) {
            return;
        }

        // if we have an isolated point (note, this point may be the first or last point in the set)
        if (!prevIsInjected && !nextIsInjected && isValidPoint(curr) && !isValidPoint(prev) && !isValidPoint(next)) {
            // create leading/trailing points to create a peak affect for isolated points
            let leadingPoint = {
                ...curr,
                y: yValue,
                x: curr.x - xOffset,
                value: null,
                $injected: true,
            };
            let trailingPoint = {
                ...curr,
                y: yValue,
                x: curr.x + xOffset,
                value: null,
                $injected: true,
            };

            // if we are the first or last point, we do not add a point before/after, respectively
            if (isFirstPoint) {
                leadingPoint = null;
            } else if (isLastPoint) {
                trailingPoint = null;
            }

            // add trailing first because it does not affect index for the leading point
            if (trailingPoint) {
                insertSeriesPoint(series, trailingPoint, index + 1);
            }

            // now add the leading point, if it exists
            if (leadingPoint) {
                insertSeriesPoint(series, leadingPoint, index);
            }
        }
    });

    return series;
}

function insertSeriesPoint(series, point, index) {
    const seriesData = _.get(series, 'data');

    if (_.isArray(seriesData)) {
        const prev = seriesData[index - 1];
        const curr = seriesData[index];

        if (_.get(prev, 'x') !== point.x && _.get(curr, 'x') !== point.x) {
            seriesData.splice(index, 0, point);
        }
    }
}

/**
 * Sync up all of the points for different series. This method is to fill up data points to make sure that there
 * are always same amount of data points for each series on the chart. This is used for both line and area charts.
 *
 * @param {Object[]} seriesList The series that this transformation should apply itself to
 */
export function syncAxisPoints(seriesList) {
    const allXValues = _(seriesList).map('data').flatten().map('x').sortBy().sortedUniq().value();
    _.each(seriesList, (series) => {
        const data = _.get(series, 'data');
        if (data?.length) {
            syncAllSeries(data, allXValues);
        }
    });
}

function insertEmptyDataPoint(data, xValue, index) {
    const newPoint = { y: 0, x: xValue, value: null, $injected: true };
    data.splice(index, 0, newPoint);
}

function syncAllSeries(data, allXValues) {
    let i = 0;
    let j = 0;
    while (i < data.length && j < allXValues.length) {
        if (allXValues[j] < data[i].x) {
            insertEmptyDataPoint(data, allXValues[j], i);
            j += 1;
        } else if (allXValues[j] === data[i].x) {
            i += 1;
            j += 1;
        } else {
            i += 1;
        }
    }
    while (j < allXValues.length) {
        insertEmptyDataPoint(data, allXValues[j], i);
        i += 1;
        j += 1;
    }
}
